import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  FileText,
  Calendar,
  MapPin,
  Clock,
  User,
  Mail,
  Phone,
  Music,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Download,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import SignaturePad from '../components/contracts/SignaturePad';
import {
  getContract,
  markContractViewed,
  signContract,
  CONTRACT_STATUS,
} from '../services/contractService';

export default function SignContract() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState(null);

  // Fetch contract on mount
  useEffect(() => {
    const fetchContract = async () => {
      if (!contractId) {
        setError('No contract ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const contractData = await getContract(contractId);
        setContract(contractData);

        // Mark as viewed if status is 'sent' (non-blocking - don't fail page load if write is denied)
        if (contractData.status === CONTRACT_STATUS.SENT) {
          markContractViewed(contractId).catch((viewErr) => {
            console.warn('Could not mark contract as viewed:', viewErr.message);
          });
        }

        // Check if already signed
        if (
          contractData.status === CONTRACT_STATUS.SIGNED ||
          contractData.status === CONTRACT_STATUS.PAID
        ) {
          setIsSigned(true);
          setSignatureUrl(contractData.signatureUrl);
        }
      } catch (err) {
        console.error('Error fetching contract:', err);
        console.error('Error code:', err.code, 'Error message:', err.message);
        if (err.code === 'permission-denied') {
          setError('Access denied. Please contact us for assistance.');
        } else {
          setError('Contract not found or has expired. Please check the link and try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchContract();
  }, [contractId]);

  const handleSign = async (signatureData) => {
    setIsSigning(true);

    try {
      // Save signature to Firestore
      await signContract(contractId, signatureData, null);

      setSignatureUrl(signatureData);
      setIsSigned(true);

      // Generate signed contract PDF for email attachment
      let contractPdfBase64 = null;
      try {
        const [{ pdf }, { default: ContractTemplate }] = await Promise.all([
          import('@react-pdf/renderer'),
          import('../components/contracts/ContractTemplate'),
        ]);
        const blob = await pdf(
          <ContractTemplate contract={contract} signatureUrl={signatureData} />
        ).toBlob();
        const arrayBuffer = await blob.arrayBuffer();
        contractPdfBase64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
      } catch (pdfErr) {
        console.error('Failed to generate PDF for email:', pdfErr);
      }

      // Build Google Calendar link
      const buildCalendarLink = () => {
        try {
          // Parse as local time to avoid UTC off-by-one
          const [y, mo, da] = String(contract.eventDate).split('-').map(Number);
          const eventDateObj = new Date(y, mo - 1, da);
          const year = eventDateObj.getFullYear();
          const month = String(eventDateObj.getMonth() + 1).padStart(2, '0');
          const day = String(eventDateObj.getDate()).padStart(2, '0');
          const dateStr = `${year}${month}${day}`;
          const title = encodeURIComponent(`${contract.artistName || 'Scooby DaGodSon'} Live Performance`);
          const location = encodeURIComponent(contract.venue || '');
          const details = encodeURIComponent(`Performance by ${contract.artistName || 'Scooby DaGodSon feat. Sirius Company'}\nPackage: ${contract.packageName || ''}\nTotal: $${Number(contract.totalAmount || 0).toLocaleString()}`);
          return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dateStr}/${dateStr}&location=${location}&details=${details}`;
        } catch {
          return null;
        }
      };

      const calendarLink = buildCalendarLink();
      const artistName = contract.artistName || 'Scooby DaGodSon feat. Sirius Company';

      // Send notification email to admin team
      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: 'siriuscompanydc@gmail.com',
            subject: `Contract Signed: ${contract.clientName}`,
            type: 'contract-signed',
            customerName: contract.clientName,
            artistName,
            venue: contract.venue || 'TBD',
            eventDate: formatDate(contract.eventDate),
            signedAt: new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }),
            totalAmount: contract.totalAmount,
            depositAmount: contract.depositAmount,
          }),
        });
      } catch (emailErr) {
        console.error('Failed to send admin notification:', emailErr);
      }

      // Send congrats email to customer with signed PDF and calendar link
      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: contract.clientEmail,
            subject: `Congratulations! Your booking with ${artistName} is confirmed`,
            type: 'contract-signed-customer',
            customerName: contract.clientName,
            artistName,
            venue: contract.venue || 'TBD',
            eventDate: formatDate(contract.eventDate),
            eventTime: formatTime(contract.eventTime),
            totalAmount: contract.totalAmount,
            depositAmount: contract.depositAmount,
            calendarLink,
            contractPdf: contractPdfBase64,
          }),
        });
      } catch (emailErr) {
        console.error('Failed to send customer congrats email:', emailErr);
      }
    } catch (err) {
      console.error('Error signing contract:', err);
      alert('Failed to sign contract: ' + (err.message || 'Please try again.'));
    } finally {
      setIsSigning(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Dynamically import PDF modules to avoid reconciler issues on page load
      const [{ pdf }, { default: ContractTemplate }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../components/contracts/ContractTemplate'),
      ]);

      const blob = await pdf(
        <ContractTemplate contract={contract} signatureUrl={signatureUrl} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${contract.id?.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      // YYYY-MM-DD from date input must be parsed as local, not UTC
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
        const [year, month, day] = String(dateStr).split('-').map(Number);
        return format(new Date(year, month - 1, day), 'MMMM d, yyyy');
      }
      return format(new Date(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount).toLocaleString()}`;
  };

  // Format time to 12-hour format (matching PDF)
  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBD';
    if (/AM|PM/i.test(timeStr)) return timeStr; // already 12h
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading contract...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Contract Not Found</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-orange-500 to-red-600"
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <FileText className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Performance Agreement
          </h1>
          <p className="text-gray-400">
            Contract #{contract?.id?.substring(0, 8).toUpperCase()}
          </p>
        </motion.div>

        {/* Success Message */}
        {isSigned && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8"
          >
            <Card className="border-green-500 bg-green-500/10">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">
                  Contract Signed Successfully!
                </h2>
                <p className="text-gray-400 mb-4">
                  Thank you for signing. You will receive a confirmation email shortly.
                </p>
                <Button
                  onClick={handleDownloadPDF}
                  className="bg-gradient-to-r from-orange-500 to-red-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Signed Contract
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Contract Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Client Info */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-orange-500" />
                    Client Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-300">
                      <User className="w-4 h-4 text-gray-500" />
                      <span>{contract?.clientName}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span>{contract?.clientEmail}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span>{contract?.clientPhone}</span>
                    </div>
                  </div>
                </div>

                {/* Event Info */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    Event Details
                  </h3>
                  <div className="space-y-3">
                    {contract?.eventType && (
                      <div className="flex items-center gap-3 text-gray-300">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="capitalize">{contract.eventType.replace('_', ' ')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-gray-300">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span>{formatDate(contract?.eventDate)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span>{formatTime(contract?.eventTime)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span>{contract?.venue || 'TBD'}</span>
                    </div>
                    {contract?.venueAddress && (
                      <div className="flex items-center gap-3 text-gray-300 pl-8">
                        <span className="text-sm text-gray-400">{contract.venueAddress}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Performance Details */}
              <div className="mt-8 pt-6 border-t border-gray-800">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Music className="w-5 h-5 text-orange-500" />
                  Performance Details
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400">Artist/Band</p>
                    <p className="text-white font-semibold">{contract?.artistName}</p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400">Package</p>
                    <p className="text-white font-semibold">{contract?.packageName}</p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400">Duration</p>
                    <p className="text-white font-semibold">{contract?.packageDuration}</p>
                  </div>
                </div>
                {/* Production Schedule */}
                {(contract?.loadInTime || contract?.soundCheckTime) && (
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {contract?.loadInTime && (
                      <div className="bg-gray-800 rounded-lg p-4">
                        <p className="text-sm text-gray-400">Load In Time</p>
                        <p className="text-white font-semibold">{formatTime(contract.loadInTime)}</p>
                      </div>
                    )}
                    {contract?.soundCheckTime && (
                      <div className="bg-gray-800 rounded-lg p-4">
                        <p className="text-sm text-gray-400">Sound Check Time</p>
                        <p className="text-white font-semibold">{formatTime(contract.soundCheckTime)}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="mt-8 pt-6 border-t border-gray-800">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-orange-500" />
                  Payment Summary
                </h3>
                <div className="bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/30 rounded-lg p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Package Price</span>
                      <span className="text-white font-semibold">
                        {formatCurrency(contract?.totalAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Deposit Required</span>
                      <span className="text-white font-semibold">
                        {formatCurrency(contract?.depositAmount)}
                      </span>
                    </div>
                    {contract?.secondPaymentAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">
                          Second Payment{contract?.secondPaymentDue ? ` (${contract.secondPaymentDue})` : ''}
                        </span>
                        <span className="text-white font-semibold">
                          {formatCurrency(contract?.secondPaymentAmount)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-400">Remaining Balance</span>
                      <span className="text-white font-semibold">
                        {formatCurrency((contract?.totalAmount || 0) - (contract?.depositAmount || 0) - (contract?.secondPaymentAmount || 0))}
                      </span>
                    </div>
                    <div className="border-t border-orange-500/30 pt-3 flex justify-between">
                      <span className="text-lg font-bold text-white">Total</span>
                      <span className="text-lg font-bold text-orange-500">
                        {formatCurrency(contract?.totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions - Full Contract */}
              <div className="mt-8 pt-6 border-t border-gray-800">
                <h3 className="text-lg font-bold text-orange-500 mb-4">Sirius Enterprise Contract - Terms and Conditions</h3>

                <div className="text-sm text-gray-300 space-y-4">
                  <p>
                    This Performance Agreement (hereinafter referred to as &quot;Agreement&quot;), dated for {formatDate(contract?.eventDate)}, is made by and between Sirius Enterprise (hereinafter referred to as &quot;Performers&quot;) and {contract?.clientName} (hereinafter referred to as &quot;Purchaser&quot;). It is mutually agreed by and between the parties that the Purchaser hereby engages the Performers, and the Performers hereby agree to perform the Engagement (hereinafter &quot;the Performance&quot;) subject to all of the terms and conditions set forth on the face of this Agreement.
                  </p>

                  {/* DATE/TIME/LOCATION */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">DATE/TIME/LOCATION</h4>
                    <p>
                      The Purchaser shall host an event on {formatDate(contract?.eventDate)}, at Venue named {contract?.venue || 'TBD'}. The Performers will provide live musical entertainment between the hours of {formatTime(contract?.eventTime)} and {formatTime(contract?.eventEndTime)}, totaling {contract?.packageDuration || '2 SETS (45 min each) or 1 SET (90 mins)'}.
                    </p>
                  </div>

                  {/* COMPENSATION */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">COMPENSATION</h4>
                    <p>
                      The Purchaser shall pay the Performers a total fee of {formatCurrency(contract?.totalAmount)} for the Performance, payable as follows: (1) a non refundable deposit of {formatCurrency(contract?.depositAmount)} due upon execution of this Agreement in order to execute and secure the date;{contract?.secondPaymentAmount ? ` (2) a second payment of ${formatCurrency(contract.secondPaymentAmount)}${contract.secondPaymentDue ? ` ${contract.secondPaymentDue}` : ''}; and (3)` : ' and (2)'} the remaining balance of {formatCurrency((contract?.totalAmount || 0) - (contract?.depositAmount || 0) - (contract?.secondPaymentAmount || 0))} due on or before the day of the Performance. Final payment is required prior to the band taking the stage.
                    </p>
                    <p className="mt-2">
                      Deposits and payments may be made via Cash, Zelle, CashApp, or secure payment link via Stripe. For payments made via Stripe, applicable processing fees will apply. Payment details are as follows: CashApp: $siriuscompanydc and Zelle: franksiriusmail@gmail.com
                    </p>
                    <p className="mt-2">
                      Failure to make any payment when due shall constitute a material breach of this Agreement.
                    </p>
                  </div>

                  {/* TRAVEL REQUIREMENTS */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">TRAVEL REQUIREMENTS</h4>
                    <p>
                      All travel, if applicable, MUST be booked through Performers Travel Agency, unless Purchaser has competitive rates that are verified and approved by Performer. If travel is being provided, the following must be included: Direct non stop flight for all performers, sleeping accommodations at Hotels rated 4-Star or above and professional ground transportation to and from venue and airport.
                    </p>
                  </div>

                  {/* ARTWORK AND ADVERTISING APPROVAL */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">ARTWORK AND ADVERTISING APPROVAL</h4>
                    <p>
                      ALL artwork, flyers, and advertisements must be approved by Performer/Management prior to any electronic or physical distribution and may not be distributed prior to payment of deposit. All advertising materials must contain the Performers logo and pictures as provided by the Performers.
                    </p>
                  </div>

                  {/* CANCELLATION/BREACH/FORCE MAJEURE */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">CANCELLATION/BREACH/FORCE MAJEURE</h4>
                    <p>
                      If the Purchaser fails to comply with any material term or financial obligation under this Agreement, the Performer reserves the right to cancel the Performance upon written notice. In the event of such cancellation due to Purchaser default, any deposits paid shall be forfeited and retained by the Performer. All deposits are non-refundable and become fully non-refundable within ninety (90) calendar days prior to the scheduled event date, as the Performer commits the date exclusively and may decline other opportunities.
                    </p>
                    <p className="mt-2">
                      If the Performers have completed one (1) set of the live performance and the Performance is thereafter canceled, interrupted, or terminated for any reason other than Performer&apos;s willful misconduct, Purchaser shall pay the Performers one hundred percent (100%) of the total contracted fee.
                    </p>
                    <p className="mt-2">
                      If the Performers&apos; obligations are delayed or prevented due to acts of God, strikes or other labor disputes, accidents, injuries, illness, transportation malfunctions, weather conditions, fire, acts of war or terrorism, force majeure events, or any other cause beyond the reasonable control of the Performers, the Performance shall be rescheduled, upon mutual agreement of Purchaser and Performer, to the first available date. In such event, all deposits shall be applied to the rescheduled Performance and shall remain non-refundable.
                    </p>
                  </div>

                  {/* INDEMNITY */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">INDEMNITY</h4>
                    <p>
                      Purchaser shall indemnify and hold Performer harmless from any and all costs and expenses (including reasonable actual legal fees) arising out of: (i) any claims and/or causes of actions or costs of any kind, asserted by any third party against Performer with respect to Performer&apos;s performance at the Event not caused by Performer&apos;s gross negligence, and/or (ii) breach of any of Purchaser&apos;s responsibilities, representations and/or obligations as set forth on the front and reverse of this Agreement.
                    </p>
                  </div>

                  {/* LICENSES/INSURANCE/SECURITY */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">LICENSES/INSURANCE/SECURITY</h4>
                    <p>
                      At Purchaser&apos;s expense, Purchaser shall obtain any and all required licenses and permits for the Performance and shall obtain and have in place all reasonable and necessary fire, liability and other insurance, as may be appropriate and/or required by law for the Performance as well as adequate security to protect Performer, Performer&apos;s property and equipment, and all persons in attendance.
                    </p>
                  </div>

                  {/* MERCHANDISE */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">MERCHANDISE</h4>
                    <p>
                      Performer shall have sole and exclusive right but not the obligation to sell souvenir merchandise and programs including audio and audiovisual recordings and the receipts thereof shall belong solely to Performer.
                    </p>
                  </div>

                  {/* SOUNDCHECK/CONTROL OF PRODUCTION */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">SOUNDCHECK/CONTROL OF PRODUCTION</h4>
                    <p>
                      Performer shall be provided reasonable time for sound check and rehearsal at venue on date of Performance and Performer shall have sole and exclusive control over all aspects of production, presentation, and routine of Performer&apos;s show for Performance, including but not limited to personnel, instrumentation and repertoire.
                    </p>
                  </div>

                  {/* SOUND/LIGHTING/STAGE REQUIREMENTS */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">SOUND/LIGHTING/STAGE REQUIREMENTS/ADDITIONAL CONTACTS</h4>
                    <p>
                      NO LIMIT Sound Company EXCLUSIVELY holds the Rider and Stage Plot for Performer and must be used by the Purchaser for ALL events. The ONLY exceptions to this requirement are if the venue has a house system that is deemed adequate and approved by the Performer. The Purchaser MUST also provide, at no cost to the Performer, adequate staging and lights. Staging and lights MUST be approved by Performers prior to Performance.
                    </p>
                    {(contract?.loadInTime || contract?.soundCheckTime) && (
                      <div className="mt-2">
                        {contract?.loadInTime && <p>Load In/Sound Check Time: {formatTime(contract.loadInTime)}</p>}
                        {contract?.soundCheckTime && <p>Sound check MUST be completed by: {formatTime(contract.soundCheckTime)}</p>}
                      </div>
                    )}
                  </div>

                  {/* ADDITIONAL REQUIREMENTS FOR PERFORMERS BY CLIENT */}
                  <div className="mt-6">
                    <h4 className="text-orange-500 font-bold mb-2">ADDITIONAL REQUIREMENTS FOR PERFORMERS BY CLIENT</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Minimum two (2) hours of setup prior to start of Performance and Purchaser shall inform Performers of the earliest available time that setup can begin.</li>
                      <li>Food if provided by caterer, sodas and/or bottled water.</li>
                      <li>A Green Room/Dressing Room.</li>
                      <li>Parking for Performers (at least 5). If venue has a parking lot.</li>
                      <li>An area for Performers to unload and/or park vehicles.</li>
                      <li>Performers are allowed at least 1 guest each, not to exceed number of band members/management.</li>
                    </ul>
                  </div>

                  {/* Special Addendum */}
                  {contract?.addendum && (
                    <div className="mt-6">
                      <div className="bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg p-4">
                        <h4 className="text-orange-500 font-bold mb-2">SPECIAL ADDENDUM</h4>
                        <p className="whitespace-pre-wrap">{contract.addendum}</p>
                      </div>
                    </div>
                  )}

                  {/* Agreement Footer */}
                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <p className="italic">
                      The undersigned parties have read and agreed to all of the terms and conditions of this Agreement including of any &quot;Additional Terms and Conditions&quot; on the attached which are deemed incorporated herein and made a part hereof and the provisions of any annexed Performance rider.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Signature Section */}
        {!isSigned && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Sign Contract</h3>
                <SignaturePad onSave={handleSign} disabled={isSigning} />
                {isSigning && (
                  <div className="flex items-center justify-center gap-2 mt-4 text-orange-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing signature...</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Signed Signature Display */}
        {isSigned && signatureUrl && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="mb-8">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-white mb-4">Your Signature</h3>
                <div className="bg-white rounded-lg p-4">
                  <img
                    src={signatureUrl}
                    alt="Your signature"
                    className="max-h-24 mx-auto"
                  />
                </div>
                <p className="text-center text-sm text-gray-400 mt-2">
                  Signed on {format(new Date(), 'MMMM d, yyyy')}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
